import { useState } from 'react'
import { AlertCircle, AlertTriangle, Loader2 } from 'lucide-react'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useAuditTrends } from '@/hooks/useAuditSearch'
import { AUDIT_CENTER_SOURCES, auditTrailLabel } from '@/lib/auditCenter'
import { cn } from '@/lib/utils'
import type { AuditCenterSource, AuditSearchParams, TrendCount, TrendInterval } from '@/types/audit'

/** One colour per trail, used by the chart and nowhere else. */
const TRAIL_COLOUR: Record<AuditCenterSource, string> = {
  user: '#64748b',
  storage: '#0891b2',
  product: '#2563eb',
  inventory: '#059669',
  equipment: '#d97706',
  procedure: '#7c3aed',
  field: '#e11d48',
  patient: '#0d9488',
  labels: '#9333ea',
  offline: '#475569',
}

const INTERVALS: { value: TrendInterval; label: string }[] = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
]

const tooltipStyle = {
  fontSize: 12,
  borderRadius: 8,
  border: '1px solid #e5e7eb',
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
} as const

function TopList({ title, rows, empty }: { title: string; rows: TrendCount[]; empty: string }) {
  const max = Math.max(1, ...rows.map((r) => r.count))
  return (
    <div className='rounded-lg border bg-card p-4'>
      <h3 className='mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>{title}</h3>
      {rows.length === 0 ? (
        <p className='text-xs text-muted-foreground'>{empty}</p>
      ) : (
        <ol className='space-y-2'>
          {rows.map((r) => (
            <li key={r.label} className='text-xs'>
              <div className='flex items-baseline justify-between gap-2'>
                <span className='min-w-0 truncate' title={r.label}>
                  {r.label}
                </span>
                <span className='shrink-0 tabular-nums text-muted-foreground'>{r.count.toLocaleString()}</span>
              </div>
              <div className='mt-1 h-1.5 rounded-full bg-muted'>
                <div className='h-1.5 rounded-full bg-primary/70' style={{ width: `${(r.count / max) * 100}%` }} />
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}

/**
 * Audit Trail trending (z8nrz7dq6q, spec page 4) for exactly what the Center
 * is searching: activity over time by trail, the fields, people, records and
 * products that change most, and the patterns worth investigating.
 */
export function AuditTrendsPanel({ params }: { params: AuditSearchParams }) {
  const [interval, setTrendInterval] = useState<TrendInterval>('day')
  const trends = useAuditTrends(params)
  const data = trends.data
  // Every interval arrives in one response: switching is a lookup, not a request.
  const summary = data?.intervals[interval]
  // A new search keeps the last trend on screen while it loads; say so, rather
  // than leave a chart that no longer matches the search looking current.
  const updating = trends.isPlaceholderData

  const trailsShown = AUDIT_CENTER_SOURCES.map((s) => s.value).filter((s) =>
    (summary?.byTrail ?? []).some((t) => t.label === s),
  )
  const chart = (summary?.series ?? []).map((p) => ({ period: p.period, ...p.byTrail }))

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='flex items-center gap-1 rounded-lg border bg-muted/40 p-0.5' role='group' aria-label='Trend interval'>
          {INTERVALS.map((i) => (
            <button
              key={i.value}
              type='button'
              aria-pressed={interval === i.value}
              onClick={() => setTrendInterval(i.value)}
              // No colour transition: the selected state changes at once, as the
              // chart does.
              className={cn(
                'rounded-md px-3 py-1 text-xs font-medium',
                interval === i.value ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {i.label}
            </button>
          ))}
        </div>
        {data && summary && (
          <p className='text-xs text-muted-foreground'>
            {updating ? (
              <>
                <Loader2 className='mr-1.5 inline h-3.5 w-3.5 animate-spin' />
                Updating for the new search…
              </>
            ) : data.sampled ? (
              `Trend of the newest ${summary.totalRows.toLocaleString()} of ${data.matched.toLocaleString()} matching changes.`
            ) : (
              `${summary.totalRows.toLocaleString()} changes in this search.`
            )}
          </p>
        )}
      </div>

      {trends.isError ? (
        <div className='flex items-center gap-2 rounded-lg border px-4 py-6 text-sm text-destructive'>
          <AlertCircle className='h-4 w-4 shrink-0' />
          Could not trend the audit trail. {trends.error.message}
        </div>
      ) : trends.isLoading ? (
        <p className='py-10 text-center text-sm text-muted-foreground'>Trending…</p>
      ) : !summary || summary.totalRows === 0 ? (
        <p className='rounded-lg border py-10 text-center text-sm text-muted-foreground'>
          No audit activity matches this search, so there is nothing to trend.
        </p>
      ) : (
        <div className={cn('space-y-4', updating && 'opacity-50')} aria-busy={updating}>
          <section className='rounded-lg border bg-card p-4' aria-label='Activity over time'>
            <h3 className='mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
              Activity per {interval}, by audit trail
            </h3>
            <div className='h-64'>
              <ResponsiveContainer width='100%' height='100%'>
                <BarChart data={chart} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='#e5e7eb' />
                  <XAxis
                    dataKey='period'
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    tickLine={false}
                    axisLine={{ stroke: '#e5e7eb' }}
                    interval='preserveStartEnd'
                    minTickGap={16}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} width={36} />
                  <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} contentStyle={tooltipStyle} />
                  <Legend iconType='square' wrapperStyle={{ fontSize: 12 }} />
                  {trailsShown.map((s) => (
                    <Bar
                      key={s}
                      dataKey={s}
                      name={auditTrailLabel(s)}
                      stackId='trail'
                      fill={TRAIL_COLOUR[s]}
                      maxBarSize={36}
                      // Bars growing from zero on every switch read as a flicker.
                      isAnimationActive={false}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className='rounded-lg border bg-card p-4' aria-label='Areas for investigation'>
            <h3 className='mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>Areas for investigation</h3>
            {summary.findings.length === 0 ? (
              <p className='text-xs text-muted-foreground'>
                Nothing in this search needs attention.
              </p>
            ) : (
              <ul className='space-y-2'>
                {summary.findings.map((f, i) => (
                  <li
                    key={i}
                    className={cn(
                      'flex items-start gap-2 rounded-md border px-3 py-2 text-sm',
                      f.severity === 'high' ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50',
                    )}
                  >
                    <AlertTriangle
                      className={cn('mt-0.5 h-4 w-4 shrink-0', f.severity === 'high' ? 'text-red-600' : 'text-amber-600')}
                    />
                    <div className='min-w-0 flex-1'>
                      <p className='font-medium'>{f.title}</p>
                      <p className='text-xs text-muted-foreground'>{f.detail}</p>
                    </div>
                    {/* Colour alone does not say how urgent it is. */}
                    <span
                      className={cn(
                        'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium',
                        f.severity === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800',
                      )}
                    >
                      {f.severity === 'high' ? 'High' : 'Medium'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-4'>
            <TopList title='Most changed fields' rows={summary.topFields} empty='No field names in these changes.' />
            <TopList title='Most active users' rows={summary.topUsers} empty='No users.' />
            <TopList title='Most changed records' rows={summary.topRecords} empty='No records.' />
            <TopList title='Most changed products' rows={summary.topProducts} empty='No products in these changes.' />
          </div>
        </div>
      )}
    </div>
  )
}
